import { PageActions } from 'shared/enums/table-page-actions';
import { IPageModelBase } from 'models/base-type';
import { ORDER_BY_ASC, ORDER_BY_DESC } from 'shared/constants/pagination';
import { Digits } from 'shared/enums/digits';
import CryptoJS from 'crypto-js';
import { EAppointmentStatus } from 'shared/enums/appointment-status';

export const CalculateBMI = (weight: number, height: number) => {
    // Convert weight from lbs to kg
    weight /= 2.20462;

    // Convert height from feet to meters
    height /= 3.28084;

    // Calculate BMI = weight (kg) / height (m)²
    const BMI = weight / Math.pow(height, 2);

    return Math.round(BMI * 100) / 100;
};

export const getDecryptKey = (value: string) => {
    return CryptoJS.AES.decrypt(value, process.env.REACT_APP_CIPHER).toString(CryptoJS.enc.Utf8);
};

export const getEncryptKey = (value: string) => {
    return CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(value), process.env.REACT_APP_CIPHER).toString();
};

export const getBaseURL = (url: string) => {
    return url + '/api/';
};

export const base64ToArrayBuffer = (base64) => {
    const binaryString = window.atob(base64);
    const binaryLen = binaryString.length;
    const bytes = new Uint8Array(binaryLen);
    for (let i = 0; i < binaryLen; i++) {
        const ascii = binaryString.charCodeAt(i);
        bytes[i] = ascii;
    }
    return bytes;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handlePageDetailChange = (action: any, value: any, pageDetails: IPageModelBase) => {
    let pageDetail: IPageModelBase = pageDetails;
    switch (action) {
        case PageActions.OrderDetailChange: {
            const isAsc = pageDetail.orderBy === value && pageDetail.order === ORDER_BY_ASC;
            pageDetail = {
                ...pageDetail,
                order: isAsc ? ORDER_BY_DESC : ORDER_BY_ASC,
                orderBy: value,
            };
            break;
        }
        case PageActions.PageChange: {
            pageDetail = {
                ...pageDetail,
                page: value,
            };
            break;
        }
        case PageActions.RowsPerPageChange: {
            pageDetail = {
                ...pageDetail,
                page: Digits.Zero,
                rowsPerPage: value,
            };
            break;
        }
    }
    return pageDetail;
};

export const replaceAll = (str, oldChar, newChar) => {
    const regex = new RegExp(`${oldChar}`, 'g');
    return str?.replace(regex, newChar);
};

export const getEventClassColor = (statusId: number) => {
    switch (statusId) {
        case EAppointmentStatus.Pending:
            return 'awaiting-event';
        case EAppointmentStatus.Confirmed:
            return 'confirmed-event';
        case EAppointmentStatus.Requested_Rescheduled:
            return 'requested-rescheduled-event';
        case EAppointmentStatus.Checked_In:
            return 'accepted-event';
        case EAppointmentStatus.Paperwork_Process:
            return 'paperwork-event';
    }
};

export const getColorBasedOnStatus = (statusId: number) => {
    switch (statusId) {
        case EAppointmentStatus.Confirmed:
            return '#243A62';
        case EAppointmentStatus.Requested_Rescheduled:
            return '#A7D8ED';
        case EAppointmentStatus.Checked_In:
            return '#f0de9e';
        case EAppointmentStatus.Pending:
            return '#f9a37f';
        case EAppointmentStatus.Paperwork_Process:
            return '#66A4C5';
    }
    return 'leave-event'; //For variable leave, notWorking-event - for notWorking
};

export const handleCheckboxClick = (event, id, selected) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }

    return newSelected;
};

export const arrayBufferToBase64 = (buffer: ArrayBuffer | ArrayLike<number>) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
};

// array2 is object of array
export const isSameArray = (array1, array2, key: string) => {
    let i,
        isSameArray = false;
    for (i = 0; i < array2?.length; i++) {
        if (array1.includes(array2[i]?.[key])) {
            isSameArray = true;
        } else {
            isSameArray = false;
            break;
        }
    }
    return isSameArray;
};

export const dateValidator = (date) => {
    if (date !== null) {
        if (new Date(date)?.toString() === 'Invalid Date') return false;
        else return true;
    }
};

export const participantDataExtractor = (name: string) => {
    const startIndex = name.indexOf('(');
    const endIndex = name.lastIndexOf(')');

    if (startIndex === -1) {
        // No opening parenthesis
        return { outside: name, inside: '' };
    }

    if (startIndex !== -1 && endIndex === -1) {
        // Opening parenthesis exists but no closing parenthesis
        return { outside: name.substring(0, startIndex), inside: '' };
    }

    // Both parentheses are present
    return {
        outside: name.substring(0, startIndex),
        inside: name.substring(startIndex + 1, endIndex),
    };
};

export const participantNameExtractor = (name: string) => {
    if (name?.includes('#')) {
        return name?.substring(0, name?.indexOf('#'));
    } else return name;
};

export const calculateFileSize = (bytes: number) => {
    if (bytes >= 1024 * 1024 * 1024) {
        return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Gb`;
    } else if (bytes >= 1024 * 1024) {
        return `${(bytes / (1024 * 1024)).toFixed(2)} Mb`;
    } else if (bytes >= 1024) {
        return `${(bytes / 1024).toFixed(2)} Kb`;
    } else {
        return `${bytes} Bytes`;
    }
};

export const toFixed = (value: number, decimals = 2) => {
    value = Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals);
    return !isNaN(value) ? value?.toFixed(decimals) : Digits.Zero.toFixed(decimals);
};

export const formatStatusString = (input: string): string => {
    return input?.replace(/_/g, ' ')?.replace(/([a-z])([A-Z])/g, '$1 $2');
};
